import React, { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { NewsItemModel } from '../../../../api-service/clients';
import { ForumNewsTabComponent } from './forum-news-tab-component';
import { isStillVisible } from './news-item-visibility';
import { allAuthors, authorFilterOptions, isWrittenBy } from './news-author-filter';
import { useForumCaseworkerContext } from '../use-forum-caseworker-context';

const client = moduleClientFactory.createForumCaseworkerClient();

export interface IForumNewsTabProps {
  canWrite: boolean;
}

export function ForumNewsTab(props: IForumNewsTabProps) {
  const { canWrite } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [newsItems, setNewsItems] = useState<NewsItemModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [chosenAuthor, setChosenAuthor] = useState(allAuthors);
  const { canAdministerNews, displayName, isNewsFileUploadEnabled } = useForumCaseworkerContext();

  const loadNewsItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await client.getNewsItems();
      setNewsItems(result.newsItems ?? []);
      setHasFailed(false);
    } catch {
      // Without this the grid would claim there is no news when the call failed.
      setHasFailed(true);
      setNewsItems([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNewsItems();
  }, [loadNewsItems]);

  async function handleCloseNewsItem(newsItem: NewsItemModel) {
    try {
      await client.closeNewsItem({ newsItemId: newsItem.id });
    } catch {
      enqueueSnackbar('Kan ikke lukke nyheden.', { variant: 'error' });
      return;
    }
    await loadNewsItems();
  }

  async function handleDeleteNewsItem(newsItem: NewsItemModel) {
    try {
      await client.deleteNewsItem({ newsItemId: newsItem.id });
    } catch {
      enqueueSnackbar('Kan ikke slette nyheden.', { variant: 'error' });
      return;
    }
    await loadNewsItems();
  }

  function handleNewsChanged() {
    loadNewsItems();
  }

  function handleShowExpiredChange(show: boolean) {
    setShowExpired(show);
  }

  function handleAuthorFilterChange(author: string) {
    setChosenAuthor(author);
  }

  const authorOptions = authorFilterOptions(newsItems, displayName);
  const authorFilter = authorOptions.some(option => option.value === chosenAuthor) ? chosenAuthor : allAuthors;
  useEffect(() => {
    if (chosenAuthor !== authorFilter) {
      setChosenAuthor(allAuthors);
    }
  }, [chosenAuthor, authorFilter]);

  const byVisibility = showExpired ? newsItems : newsItems.filter(isStillVisible);
  const byAuthor = byVisibility.filter(newsItem => isWrittenBy(newsItem, authorFilter, displayName));

  return (
    <ForumNewsTabComponent
      newsItems={byAuthor}
      isLoading={isLoading}
      hasFailed={hasFailed}
      showExpired={showExpired}
      canWrite={canWrite}
      canShowToAll={canAdministerNews}
      canAttachFiles={isNewsFileUploadEnabled}
      onShowExpiredChange={handleShowExpiredChange}
      onNewsChanged={handleNewsChanged}
      onCloseNewsItem={handleCloseNewsItem}
      onDeleteNewsItem={handleDeleteNewsItem}
      authorFilterOptions={authorOptions}
      authorFilter={authorFilter}
      onAuthorFilterChange={handleAuthorFilterChange}
    />
  );
}
